import type { ReactNode } from 'react';
import { Pencil, Wrench } from 'lucide-react';
import { Button, cn } from '@herit-fe/heritage-ui';
import { UI_TEXT } from '@/shared/languages';

/**
 * InfoRow 트레일링 액션 — 정보 상세 SectionRow(구앱 이식): 발전소 이름 수정·용도 변경·계약 입력·인버터 자세히.
 * 버튼 표현은 **값 유무**로 갈린다 — 값 없음=filled '입력'(입력 유도), 값 있음=outline+종류 라벨.
 * 예외는 `detail` — 값이 있어도 filled(primary)다. 나머지 액션이 같은 화면 안의 편집(시트)인 데 반해 detail 만
 * **화면을 넘기는 이동**이라, 행에 딸린 보조 버튼이 아니라 그 행의 목적지로 읽혀야 한다.
 *
 * 구앱의 `call`(시공사 `tel:` 걸기)은 소비처와 함께 제거했다 — 되살릴 땐 아이콘·라벨·UI_TEXT 키를 같이 복원할 것.
 */
export interface InfoRowAction {
  type: 'edit' | 'change' | 'create' | 'detail';
  /** 버튼 클릭 — 시트 열기·라우팅 등 소비처 소유. */
  onPress: () => void;
}

export interface InfoRowProps {
  label: string;
  value: ReactNode;
  /** 흐림(운영준비중 섹션) — 라벨/값 색을 subtle 로. 액션 버튼은 그대로 활성(구앱 정합). */
  muted?: boolean;
  /** 트레일링 액션 버튼(선택) — 없으면 기존 라벨+값 행 그대로. */
  action?: InfoRowAction;
}

// 종류별 아이콘(구앱 SectionRow: detail=wrench·그 외=pencil). 값 없음(입력 유도)은 항상 pencil.
const ACTION_ICON = { edit: Pencil, change: Pencil, create: Pencil, detail: Wrench };
const ACTION_LABEL = {
  edit: UI_TEXT.PLANT_INFO_ACTION_EDIT,
  change: UI_TEXT.COMMON_CHANGE,
  create: UI_TEXT.PLANT_INFO_ACTION_CREATE,
  detail: UI_TEXT.COMMON_DETAIL,
};

function InfoRowActionButton({ action, isEmpty }: { action: InfoRowAction; isEmpty: boolean }) {
  // 값 없음 = 입력 유도(filled '입력'). 값 있음 = 종류 라벨(outline). 구앱 SectionRow(`!value` 분기) 정합.
  const create = isEmpty || action.type === 'create';
  const Icon = create ? Pencil : ACTION_ICON[action.type];
  const label = create ? UI_TEXT.PLANT_INFO_ACTION_CREATE : ACTION_LABEL[action.type];
  // detail 은 값이 있어도 primary — 유일하게 화면을 넘기는 액션이라 outline 으로 두면 진입점이 묻힌다.
  const filled = create || action.type === 'detail';
  return (
    <Button
      type="button"
      variant={filled ? 'default' : 'outline'}
      size="sm"
      onClick={action.onPress}
      className="shrink-0 gap-1"
    >
      <Icon className="size-3.5" />
      {label}
    </Button>
  );
}

/**
 * 정보 행 — **좌 라벨 / 우 값**, 하단 보더로 끊는다. 부모는 `InfoList`(`dl`)가 소유한다.
 *
 * **라벨과 값의 차이는 굵기 하나뿐이다**(라벨 `font-normal` / 값 `font-semibold`) — 크기·색은 같은
 * `text-sm text-foreground`. 예전엔 라벨이 `font-medium`+muted, 값이 기본 굵기+foreground 라 **굵기는 라벨이,
 * 색은 값이** 강조돼 두 신호가 상쇄됐고 어느 쪽이 값인지 읽히지 않았다. 신호를 굵기 하나로 몰아 방향을 통일한다.
 *
 * **반응형**: `sm` 이상은 한 줄에 좌 라벨 / 우 값(`text-right`) — 수익 상세시트 정합, 수치가 오른쪽에서 자릿수를
 * 맞춰 읽힌다. `sm` 미만은 값이 **아래 줄로 떨어지고 좌측 정렬** — 좁은 폭에서 라벨과 값이 한 줄을 나눠 가지면
 * 긴 값이 두세 글자씩 끊겨 접힌다. 줄을 나누면 값이 폭을 온전히 쓰고, 좌측 정렬이라 라벨과 시작선이 맞는다
 * (굵기 대비가 위계를 지므로 들여쓰기 없이도 읽힌다).
 *
 * 확장(정보 상세): `muted`(운영준비중 섹션 흐림) · `action`(트레일링 버튼, 구앱 SectionRow). `action` 이 있으면
 * 값이 비었을 때 '미입력'(subtle) + filled '입력' 버튼으로 입력을 유도한다. `action` 없는 행의 빈 값은 기존대로
 * `COMMON_EMPTY_VALUE`('-') 폴백(기존 소비처 불변).
 */
export function InfoRow({ label, value, muted, action }: InfoRowProps) {
  const isEmpty = value == null || value === '';
  const displayValue = isEmpty ? (
    action ? (
      <span className="font-normal text-heviton-fg-subtle">{UI_TEXT.PLANT_INFO_NOT_ENTERED}</span>
    ) : (
      UI_TEXT.COMMON_EMPTY_VALUE
    )
  ) : (
    value
  );
  return (
    // 보더를 행이 직접 그린다(부모 `divide-y` 아님) — 조건부 렌더로 행이 빠져도 `last:` 판정이 안 어긋난다.
    // 모바일=세로 스택(값이 아랫줄) / sm↑=한 줄 좌우 배치.
    <div className="flex flex-col gap-1 border-b border-border/50 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <dt
        className={cn(
          'shrink-0 text-sm font-normal',
          muted ? 'text-heviton-fg-subtle' : 'text-foreground',
        )}
      >
        {label}
      </dt>
      {/* 값 + (선택) 액션 버튼. 모바일은 값 좌측·버튼을 줄 오른쪽 끝으로 밀고(`justify-between`),
          sm↑ 은 값·버튼이 함께 행 오른쪽에 붙는다(`justify-end`). */}
      <div className="flex min-w-0 items-center justify-between gap-2 sm:justify-end">
        <dd
          className={cn(
            'min-w-0 text-sm font-semibold sm:text-right',
            muted ? 'text-heviton-fg-subtle' : 'text-foreground',
          )}
        >
          {displayValue}
        </dd>
        {action ? <InfoRowActionButton action={action} isEmpty={isEmpty} /> : null}
      </div>
    </div>
  );
}
